/**
 * GEDCOM FAM Parser
 *
 * Extracts and parses family (FAM) records from GEDCOM tree.
 * Handles HUSB, WIFE, CHIL, MARR, and DIV tags.
 *
 * Part of Phase 4 - Slice 3 & 4: FAM Record Parsing + Date Parsing
 */

import type { TreeNode } from '../types/TreeNode';
import type { ParsedFamily } from '../types/GedcomFamily';
import { parseGedcomDate } from './dateParser';

/**
 * Extract and parse all FAM records from GEDCOM tree
 *
 * @param tree - Root nodes of GEDCOM tree
 * @returns Array of parsed family records
 */
export const extractFamRecords = (tree: TreeNode[]): ParsedFamily[] => {
  const families: ParsedFamily[] = [];

  // Find all FAM record nodes
  const famNodes = tree.filter((node) => node.tag === 'FAM' && node.pointer !== null);

  famNodes.forEach((famNode) => {
    try {
      const family = parseFamNode(famNode);
      if (family) {
        families.push(family);
      }
    } catch (error) {
      console.warn(
        `[GEDCOM Parser] Failed to parse FAM record ${famNode.pointer} at line ${famNode.lineNumber}:`,
        error
      );
    }
  });

  return families;
};

/**
 * Parse a single FAM node into ParsedFamily
 *
 * @param famNode - FAM TreeNode
 * @returns ParsedFamily or null if required data is missing
 */
const parseFamNode = (famNode: TreeNode): ParsedFamily | null => {
  if (!famNode.pointer) {
    console.warn(`[GEDCOM Parser] FAM node at line ${famNode.lineNumber} has no pointer`);
    return null;
  }

  const familyId = famNode.pointer;

  // Initialize family data
  let husbandId: string | null = null;
  let wifeId: string | null = null;
  const childrenIds: string[] = [];

  // Parse child tags
  famNode.children.forEach((child) => {
    switch (child.tag) {
      case 'HUSB':
        // Husband/father pointer
        if (child.value && child.value.trim()) {
          husbandId = child.value.trim();
        }
        break;

      case 'WIFE':
        // Wife/mother pointer
        if (child.value && child.value.trim()) {
          wifeId = child.value.trim();
        }
        break;

      case 'CHIL':
        // Child pointer (can have multiple)
        if (child.value && child.value.trim()) {
          childrenIds.push(child.value.trim());
        }
        break;

      // Other tags handled below (MARR, DIV, etc.)
      default:
        // Silently skip unknown tags
        break;
    }
  });

  // Find MARR child and extract DATE
  const marrNode = famNode.children.find((child) => child.tag === 'MARR');
  const marrDateNode = marrNode?.children.find((child) => child.tag === 'DATE');
  const marrParsed = parseGedcomDate(marrDateNode?.value);

  // Find DIV child and extract DATE
  const divNode = famNode.children.find((child) => child.tag === 'DIV');
  const divDateNode = divNode?.children.find((child) => child.tag === 'DATE');
  const divParsed = parseGedcomDate(divDateNode?.value);

  // Return family even if spouse data is missing (single-parent families are valid)
  return {
    familyId,
    husbandId,
    wifeId,
    childrenIds,
    marriageDate: marrParsed?.date || null,
    marriageYear: marrParsed?.year || null,
    marriageQualifier: marrParsed?.qualifier || null,
    divorceDate: divParsed?.date || null,
    divorceYear: divParsed?.year || null,
    divorceQualifier: divParsed?.qualifier || null,
  };
};
